import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import { getRequest } from 'utils/request';
import { Faq } from 'types/FaqQuestions';
import { isSimpleMiningUrl, getDomainName } from 'utils/isSimpleMiningUrl';
import MiningContent from './MiningContent';
import ComputingContent from './ComputingContent';
import { useRouter } from 'next/router';
import { getCanonicalUrl, getMetaTranslationKey } from 'utils/getPageMetadata';

interface Props {
  data: Faq;
}

const Support = (props: Props) => {
  const { data } = props;
  const { locale = 'en' } = useRouter();

  const { t } = useTranslation();

  return (
    <>
      <Head>
        <title>
          {t('support.meta.title', { domain: getDomainName() })?.trim() ||
            'Frequently Asked Questions | SimplePod.ai'}
        </title>
        <meta
          name="description"
          content={t(`support.meta.description.${getMetaTranslationKey()}`)}
        />
        <meta
          name="keywords"
          content={
            t('support.meta.keywords') ||
            // eslint-disable-next-line quotes
            "Check out our FAQ section - we'll clear all your doubts"
          }
        />
        <link rel="canonical" href={getCanonicalUrl(locale, 'support')} />
      </Head>
      {isSimpleMiningUrl() ? (
        <MiningContent data={data} />
      ) : (
        <ComputingContent />
      )}
    </>
  );
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export async function getStaticProps(context) {
  const {
    locale = 'en',
    params: { support },
  } = context;

  if (support) {
    return {
      notFound: true,
    };
  }

  let data = {
    groups: [],
    questions: [],
  };

  try {
    const response = await getRequest('/faq-questions', {
      locale,
    });

    if (response.data) {
      data = response.data;
    }
  } catch (error) {
    console.error(error);
  }

  return {
    props: {
      data,
      ...(await serverSideTranslations(locale)),
    },
    revalidate: 600,
  };
}

export default Support;
